import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faSyncAlt, faUser, faGraduationCap, faDollarSign, faCalendarAlt,
  faInfoCircle, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  console.log(`📡 Réponse brute :`, text.substring(0, 200));

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 2. CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('HistoriqueAchatCacheDB');
db.version(1).stores({
  history: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedHistory(key, allowExpired = false) {
  try {
    const entry = await db.history.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedHistory(key, data) {
  await db.history.put({ key, data, timestamp: Date.now() });
}

async function getStaleHistory(key) {
  return getCachedHistory(key, true);
}

// ============================================================
// 3. CONFIGURATION API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}achat/api_historique_achat.php`;

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueAchat = ({ userId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // ============================================================
  // 5. GESTION DES TOASTS
  // ============================================================
  const showToast = (message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  };

  // ============================================================
  // 6. RÉCUPÉRATION DE L'HISTORIQUE (avec cache, 429, retry)
  // ============================================================
  const fetchHistory = async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const cacheKey = `page_${page}_search_${search}`;

    try {
      // 1. Cache si non forcé
      if (!forceRefresh) {
        const cached = await getCachedHistory(cacheKey);
        if (cached) {
          setHistory(cached.data);
          setPagination(cached.pagination);
          setDataSource('cache');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      // 2. Requête réseau
      const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
      const response = await fetch(url, { signal: controller.signal });

      // 3. Gestion 429
      if (response.status === 429) {
        const stale = await getStaleHistory(cacheKey);
        if (stale) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
          setLoading(false);
          setRefreshing(false);
          return;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
        setTimeout(() => fetchHistory(page, search, forceRefresh, retryCount + 1), waitTime);
        return;
      }

      // 4. Gestion des autres erreurs HTTP
      if (!response.ok) {
        const stale = await getStaleHistory(cacheKey);
        if (stale) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur réseau – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // 5. Lecture JSON avec fetchJSON
      const data = await fetchJSON(url, { signal: controller.signal });
      if (data.status === 'success') {
        const payload = { data: data.data, pagination: data.pagination };
        setHistory(data.data);
        setPagination(data.pagination);
        const source = forceRefresh ? 'live (forcé)' : 'live';
        setDataSource(source);
        await setCachedHistory(cacheKey, payload);
        if (forceRefresh) {
          showToast('Données actualisées', 'success');
        }
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('fetchHistory error:', err);
      const stale = await getStaleHistory(cacheKey);
      if (stale) {
        setHistory(stale.data);
        setPagination(stale.pagination);
        setDataSource('cache (périmé)');
        showToast('Erreur – Utilisation du cache', 'warning');
      } else {
        showToast(err.message || 'Erreur de chargement', 'danger');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ============================================================
  // 7. RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchHistory(1, val, false);
    }, 400);
  };

  const handleRefresh = () => {
    if (!refreshing && !loading) {
      fetchHistory(pagination.currentPage, searchTerm, true);
    }
  };

  const handleRefreshForce = () => {
    if (!refreshing && !loading) {
      db.history.clear();
      fetchHistory(pagination.currentPage, searchTerm, true);
    }
  };

  // ============================================================
  // 8. INITIALISATION
  // ============================================================
  useEffect(() => {
    fetchHistory(1, '', false);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // ============================================================
  // 9. EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!history.length) {
      showToast('Aucune donnée à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Facture ID', 'Client', 'Formation', 'Montant', 'Date', 'Détails', 'Utilisateur'];
    const rows = history.map(h => [
      h.id,
      h.facture_id,
      h.client_nom || h.client_id,
      h.formation_titre || h.formation_id,
      h.montant.toFixed(2),
      h.date_achat,
      h.details,
      h.userId || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_achat_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // 10. OUVERTURE DE LA MODALE DE DÉTAILS
  // ============================================================
  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  // ============================================================
  // 11. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !history.length && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement de l'historique...</p>
      </div>
    );
  }

  // ============================================================
  // 12. RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE ACHAT
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-achat-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 10px 24px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.05);
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
        }
        .glass-table th {
          padding: 15px 12px;
          text-align: left;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary {
          color: var(--text-secondary);
        }
        .text-muted {
          color: var(--text-muted);
        }
        .text-success {
          color: var(--accent-blue);
        }
        .text-danger {
          color: #ef4444;
        }
        .fw-bold {
          font-weight: 600;
        }
        .small {
          font-size: 0.85rem;
        }
        .m-0 {
          margin: 0;
        }
        .mb-4 {
          margin-bottom: 1.5rem;
        }
        .me-1 {
          margin-right: 0.25rem;
        }
        .me-2 {
          margin-right: 0.5rem;
        }
        .p-2 {
          padding: 0.5rem;
        }
        .p-3 {
          padding: 1rem;
        }
        .py-5 {
          padding-top: 3rem;
          padding-bottom: 3rem;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .gap-3 {
          gap: 1rem;
        }
        .d-flex {
          display: flex;
        }
        .flex-wrap {
          flex-wrap: wrap;
        }
        .align-items-center {
          align-items: center;
        }
        .justify-content-between {
          justify-content: space-between;
        }
        .text-end {
          text-align: right;
        }
        .text-center {
          text-align: center;
        }
        .overflow-hidden {
          overflow: hidden;
        }
        .table-responsive {
          overflow-x: auto;
        }
        .border-top {
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .border-opacity-10 {
          opacity: 0.1;
        }
        .ms-auto {
          margin-left: auto;
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .historique-achat-page { padding: 16px; }
          .glass-card { padding: 1.2rem; }
          .glass-table th, .glass-table td { font-size: 0.7rem; padding: 8px 6px; }
        }
      `}</style>

      <div className="historique-achat-page">
        {/* Header avec badge source et boutons */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">Historique des achats</h1>
            <p className="text-secondary m-0">Consultez toutes les transactions enregistrées.</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn outline"
              onClick={handleRefreshForce}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={history.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="glass-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '400px' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (client, formation, détail...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Table */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Facture</th>
                  <th>Client</th>
                  <th>Formation</th>
                  <th>Montant</th>
                  <th>Date</th>
                  <th>Détails</th>
                  <th>Utilisateur</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !history.length && !refreshing ? (
                  <tr><td colSpan="9" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-5 text-secondary">Aucun enregistrement trouvé.</td></tr>
                ) : (
                  history.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td><span className="text-secondary">#{item.facture_id}</span></td>
                      <td>
                        <span className="text-secondary">
                          <FontAwesomeIcon icon={faUser} className="me-1" />
                          {item.client_nom || item.client_id}
                        </span>
                      </td>
                      <td>
                        <span className="text-secondary">
                          <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                          {item.formation_titre || item.formation_id}
                        </span>
                      </td>
                      <td><span className="text-secondary">{item.montant.toFixed(2)} $</span></td>
                      <td><span className="text-secondary">{new Date(item.date_achat).toLocaleString()}</span></td>
                      <td><span className="text-secondary" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.details}</span></td>
                      <td><span className="text-secondary">{item.userId || '-'}</span></td>
                      <td className="text-end">
                        <button className="monetra-btn p-2" onClick={() => openDetailModal(item)}>
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage - 1, searchTerm)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage + 1, searchTerm)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de détails */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faInfoCircle} className="text-success me-2" />
            Détails de l'enregistrement #{selectedRecord?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-secondary small">ID</div>
                <div>#{selectedRecord.id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Facture</div>
                <div>#{selectedRecord.facture_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Client</div>
                <div>{selectedRecord.client_nom || selectedRecord.client_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Formation</div>
                <div>{selectedRecord.formation_titre || selectedRecord.formation_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Montant</div>
                <div>{selectedRecord.montant.toFixed(2)} $</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Date</div>
                <div>{new Date(selectedRecord.date_achat).toLocaleString()}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Utilisateur</div>
                <div>{selectedRecord.userId || '-'}</div>
              </div>
              <div className="col-12">
                <div className="text-secondary small">Détails</div>
                <div>{selectedRecord.details}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)} className="monetra-btn outline">Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast de notification */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: '300px' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriqueAchat;